import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import ActivityTracker from "@/components/ActivityTracker";
import { useMe } from "@/hooks/useAuth";
import { useTenantLayout } from "@/hooks/useTenantLayout";
import { useTenantTheme } from "@/hooks/useTenantTheme";
import { useFavicon } from "@/hooks/useFavicon";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { AdminShell } from "@/components/layout/AdminShell";
import { TraderShell } from "@/components/layout/TraderShell";
import { ManagerShell } from "@/components/layout/ManagerShell";
import { Skeleton } from "@/components/ui/Skeleton";

// Auth flow.
const LoginPage = lazy(() => import("@/pages/auth/Login"));
const RegisterPage = lazy(() => import("@/pages/auth/Register"));
const ActivatePage = lazy(() => import("@/pages/auth/Activate"));
const ForgotPasswordPage = lazy(() => import("@/pages/auth/ForgotPassword"));

// Broker admin (per-tenant).
const AdminDashboardPage = lazy(() => import("@/pages/admin/Dashboard"));
const TraderAdminPage = lazy(() => import("@/pages/admin/TraderAdmin"));
const BankRequestsPage = lazy(() => import("@/pages/admin/BankRequests"));
const DepositRequestsPage = lazy(() => import("@/pages/admin/DepositRequests"));
const WithdrawRequestsPage = lazy(() => import("@/pages/admin/WithdrawRequests"));
const KycRequestsPage = lazy(() => import("@/pages/admin/KycRequests"));
const ActivityLogReportPage = lazy(() => import("@/pages/admin/reports/ActivityLog"));
const TraderDepositReportPage = lazy(() => import("@/pages/admin/reports/TraderDepositReport"));
const TraderWithdrawReportPage = lazy(() => import("@/pages/admin/reports/TraderWithdrawReport"));
const LeadsPage = lazy(() => import("@/pages/admin/Leads"));
const BalanceTransfersPage = lazy(() => import("@/pages/admin/BalanceTransfers"));
const CompanyBanksPage = lazy(() => import("@/pages/admin/CompanyBanks"));
const BonusPackagesPage = lazy(() => import("@/pages/admin/BonusPackages"));
const BonusGrantPage = lazy(() => import("@/pages/admin/BonusGrant"));
const BonusReportPage = lazy(() => import("@/pages/admin/BonusReport"));
const AnnouncementsPage = lazy(() => import("@/pages/admin/Announcements"));
const SupportPage = lazy(() => import("@/pages/admin/Support"));
const EmailTemplatesPage = lazy(() => import("@/pages/admin/EmailTemplates"));
const AdminGroupsPage = lazy(() => import("@/pages/admin/AdminGroups"));
const AdminListPage = lazy(() => import("@/pages/admin/AdminList"));
const ModuleControlPage = lazy(() => import("@/pages/admin/ModuleControl"));
const ManagerListPage = lazy(() => import("@/pages/admin/ManagerList"));
const OnlineUsersReportPage = lazy(() => import("@/pages/admin/reports/OnlineUsers"));
const BlockedUsersReportPage = lazy(() => import("@/pages/admin/reports/BlockedUsers"));
const LedgerReportPage = lazy(() => import("@/pages/admin/reports/LedgerReport"));
const TenantSettingsPage = lazy(() => import("@/pages/admin/TenantSettings"));
const PaymentGatewayPage = lazy(() => import("@/pages/admin/settings/PaymentGateway"));
const SecuritySettingsPage = lazy(() => import("@/pages/admin/settings/Security"));
const ApiConfigPage = lazy(() => import("@/pages/admin/settings/ApiConfig"));
const FinanceSetupPage = lazy(() => import("@/pages/admin/settings/FinanceSetup"));
const PlatformsSetupPage = lazy(() => import("@/pages/admin/settings/PlatformsSetup"));
const BannerSetupPage = lazy(() => import("@/pages/admin/settings/BannerSetup"));
const BankSettingPage = lazy(() => import("@/pages/admin/settings/BankSetting"));
const CurrencySetupPage = lazy(() => import("@/pages/admin/settings/CurrencySetup"));
const CurrencyPairPage = lazy(() => import("@/pages/admin/settings/CurrencyPair"));
const OtpSetupPage = lazy(() => import("@/pages/admin/settings/OtpSetup"));
const ChatbotPage = lazy(() => import("@/pages/admin/settings/Chatbot"));
const MobileAppPage = lazy(() => import("@/pages/admin/settings/MobileApp"));
const SoftwarePage = lazy(() => import("@/pages/admin/settings/Software"));
const TraderConfigPage = lazy(() => import("@/pages/admin/settings/TraderConfig"));
const TraderSidebarSettingsPage = lazy(() => import("@/pages/admin/TraderSidebarSettings"));
const NotifTemplatesPage = lazy(() => import("@/pages/admin/settings/NotifTemplates"));
const NotifUserPage = lazy(() => import("@/pages/admin/settings/NotifUser"));
const NotifAdminPage = lazy(() => import("@/pages/admin/settings/NotifAdmin"));
const RiskDashboardPage = lazy(() => import("@/pages/admin/risk/Dashboard"));
const RiskExposurePage = lazy(() => import("@/pages/admin/risk/Exposure"));
const RiskMarginPage = lazy(() => import("@/pages/admin/risk/Margin"));
const RiskAntiHedgePage = lazy(() => import("@/pages/admin/risk/AntiHedge"));
const RiskSuspiciousPage = lazy(() => import("@/pages/admin/risk/Suspicious"));
const RiskReportsPage = lazy(() => import("@/pages/admin/risk/Reports"));
const CompFatcaPage = lazy(() => import("@/pages/admin/compliance/Fatca"));
const CompCrsPage = lazy(() => import("@/pages/admin/compliance/Crs"));
const CompMifidPage = lazy(() => import("@/pages/admin/compliance/MifidExport"));
const CompAmlPage = lazy(() => import("@/pages/admin/compliance/AmlAudit"));
const CompSarPage = lazy(() => import("@/pages/admin/compliance/Sar"));
const CompSanctionsPage = lazy(() => import("@/pages/admin/compliance/Sanctions"));
const CompCalendarPage = lazy(() => import("@/pages/admin/compliance/Calendar"));
const RepExternalFundTransfer = lazy(() => import("@/pages/admin/reports/ExternalFundTransfer"));
const RepIndividualLedger = lazy(() => import("@/pages/admin/reports/IndividualLedger"));
const RepCallLogs = lazy(() => import("@/pages/admin/reports/CallLogs"));
const FinBalance = lazy(() => import("@/pages/admin/finance/Balance"));
const FinCredit = lazy(() => import("@/pages/admin/finance/Credit"));
const CreditReportPage = lazy(() => import("@/pages/admin/CreditReport"));
const FinFund = lazy(() => import("@/pages/admin/finance/Fund"));
const FinAdminDeposit = lazy(() => import("@/pages/admin/finance/AdminDeposit"));
const FinAdminWithdraw = lazy(() => import("@/pages/admin/finance/AdminWithdraw"));
const FtInternal = lazy(() => import("@/pages/admin/fund-transfer/Internal"));
const FtExternal = lazy(() => import("@/pages/admin/fund-transfer/External"));
const ReqUpi = lazy(() => import("@/pages/admin/requests/Upi"));
const OffLoyalty = lazy(() => import("@/pages/admin/offers/Loyalty"));
const OffLoyaltyLedger = lazy(() => import("@/pages/admin/offers/LoyaltyLedger"));
const CategoriesPage = lazy(() => import("@/pages/admin/Categories"));
const KycSettingsPage = lazy(() => import("@/pages/admin/kyc/KycSettings"));
const KycUploadPage = lazy(() => import("@/pages/admin/kyc/KycUpload"));
const KycReportsPage = lazy(() => import("@/pages/admin/kyc/KycReports"));
const ManageClientHub = lazy(() => import("@/pages/admin/ManageClientPages"));
const AdminMiscHub = lazy(() => import("@/pages/admin/AdminMiscPages"));
const AdminProfilePage = lazy(() => import("@/pages/admin/AdminProfilePage"));
const NotificationsPage = lazy(() => import("@/pages/admin/Notifications"));
const ContestDashboardPage = lazy(() => import("@/pages/admin/contest/ContestDashboard"));
const CreateContestPage = lazy(() => import("@/pages/admin/contest/CreateContest"));
const GroupsHubPage = lazy(() => import("@/pages/admin/groups/GroupsHub"));
const MlHubPage = lazy(() => import("@/pages/admin/ml/MlHub"));
const WhatsAppHubPage = lazy(() => import("@/pages/admin/whatsapp/WhatsAppHub"));
const Mt5HubPage = lazy(() => import("@/pages/admin/mt5/Mt5Hub"));
const Mt5ConnectionPage = lazy(() => import("@/pages/admin/mt5/Connection"));
const Mt5AccountsPage = lazy(() => import("@/pages/admin/mt5/Accounts"));
const Mt5TradeActionsPage = lazy(() => import("@/pages/admin/mt5/TradeActions"));
const Mt5TradeReportPage = lazy(() => import("@/pages/admin/mt5/TradeReport"));
const Mt5CreateAccountPage = lazy(() => import("@/pages/admin/mt5/CreateAccount"));
const ModuleStub = lazy(() => import("@/pages/admin/ModuleStub"));

// Trader portal.
const TraderDashboardPage = lazy(() => import("@/pages/trader/Dashboard"));
const TraderBanksPage = lazy(() => import("@/pages/trader/Banks"));
const TraderDepositPage = lazy(() => import("@/pages/trader/Deposit"));
const TraderWithdrawPage = lazy(() => import("@/pages/trader/Withdraw"));
const TraderKycPage = lazy(() => import("@/pages/trader/Kyc"));
const TraderWebTraderPage = lazy(() => import("@/pages/trader/WebTrader"));
const TraderAccountsPage = lazy(() => import("@/pages/trader/Accounts"));
const TraderTradeHistoryPage = lazy(() => import("@/pages/trader/TradeHistory"));
const TraderProfilePage = lazy(() => import("@/pages/trader/Profile"));
const TraderAccountSettingsPage = lazy(() => import("@/pages/trader/AccountSettings"));
const TraderFundTransferPage = lazy(() => import("@/pages/trader/FundTransfer"));
const TraderBonusPage = lazy(() => import("@/pages/trader/Bonus"));
const TraderSupportPage = lazy(() => import("@/pages/trader/Support"));
const TraderChatbotPage = lazy(() => import("@/pages/trader/Chatbot"));
const TraderComingSoonPage = lazy(() => import("@/pages/trader/ComingSoon"));


const ManagerDashboardPage = lazy(() => import("@/pages/manager/ManagerDashboard"));
const ManagerClientsPage = lazy(() => import("@/pages/manager/ManagerClients"));
const ManagerTeamPage = lazy(() => import("@/pages/manager/ManagerTeam"));
const ManagerClientDetailPage = lazy(() => import("@/pages/manager/ManagerClients").then((m) => ({ default: m.ManagerClientDetailPage })));
const ManagerReportsPage = lazy(() => import("@/pages/manager/ManagerReports"));
const ManagerActivityPage = lazy(() => import("@/pages/manager/ManagerActivity"));

const PageLoading = (
  <div className="space-y-3">
    <Skeleton className="h-10 w-1/3" />
    <Skeleton className="h-64 w-full" />
  </div>
);

/** Land users on the right shell for their role. */
function RootRedirect() {
  useTenantLayout(undefined);
  const { data, isPending } = useMe();
  if (isPending) return null;
  if (!data) return <Navigate to="/login" replace />;
  switch (data.role) {
    case "admin":
      return <Navigate to="/admin/dashboard" replace />;
    case "trader":
      return <Navigate to="/trader/dashboard" replace />;
    default:
      // Manager / unknown — bounce to login.
      return <Navigate to="/login" replace />;
  }
}

const wrap = (node: React.ReactNode) => <Suspense fallback={PageLoading}>{node}</Suspense>;

export default function App() {
  // Subdomain-aware theming: reads tenant primaryColor and injects it
  // onto :root so every component (login button, sidebar accents,
  // gradients, etc.) flips to the broker's brand color automatically.
  useTenantTheme();
  useFavicon();
  return (
    <>
    <ImpersonationBanner />
    <ActivityTracker />
    <Routes>
      <Route path="/" element={<RootRedirect />} />

      {/* ─── Auth ─────────────────────────────────────────────────── */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={wrap(<LoginPage />)} />
        <Route path="/register" element={wrap(<RegisterPage />)} />
        <Route path="/activate" element={wrap(<ActivatePage />)} />
        <Route path="/forgot-password" element={wrap(<ForgotPasswordPage />)} />
      </Route>

      {/* Platform (multi-broker super-admin) portal removed — single-broker build. */}

      {/* ─── Broker Admin ─────────────────────────────────────────── */}
      <Route path="/admin" element={<AdminShell />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={wrap(<AdminDashboardPage />)} />
        <Route path="manage-client/trader-admin" element={wrap(<TraderAdminPage />)} />
        <Route path="requests/bank" element={wrap(<BankRequestsPage />)} />
        <Route path="requests/deposit" element={wrap(<DepositRequestsPage />)} />
        <Route path="requests/withdraw" element={wrap(<WithdrawRequestsPage />)} />
        <Route path="kyc/request" element={wrap(<KycRequestsPage />)} />
        <Route path="reports/activity-log" element={wrap(<ActivityLogReportPage />)} />
        <Route path="reports/trader-deposit" element={wrap(<TraderDepositReportPage />)} />
        <Route path="reports/trader-withdraw" element={wrap(<TraderWithdrawReportPage />)} />
        <Route path="leads" element={wrap(<LeadsPage />)} />
        <Route path="requests/balance-transfer" element={wrap(<BalanceTransfersPage />)} />
        <Route path="banks/company" element={wrap(<CompanyBanksPage />)} />
        <Route path="offers/packages" element={wrap(<BonusPackagesPage />)} />
        <Route path="offers/create" element={wrap(<BonusGrantPage />)} />
        <Route path="offers/request" element={wrap(<BonusGrantPage />)} />
        <Route path="offers/report" element={wrap(<BonusReportPage />)} />
        <Route path="offers/log" element={wrap(<BonusReportPage />)} />
        <Route path="announcements" element={wrap(<AnnouncementsPage />)} />
        <Route path="support" element={wrap(<SupportPage />)} />
        <Route path="email" element={wrap(<EmailTemplatesPage />)} />
        <Route path="admins/groups" element={wrap(<AdminGroupsPage />)} />
        <Route path="admins/list" element={wrap(<AdminListPage />)} />
        <Route path="modules" element={wrap(<ModuleControlPage />)} />
        <Route path="admins/rights" element={wrap(<AdminGroupsPage />)} />
        <Route path="managers/list" element={wrap(<ManagerListPage />)} />
        <Route path="managers/add" element={wrap(<ManagerListPage />)} />
        <Route path="managers/groups" element={wrap(<AdminGroupsPage />)} />
        <Route path="managers/rights" element={wrap(<AdminGroupsPage />)} />
        <Route path="reports/online-users" element={wrap(<OnlineUsersReportPage />)} />
        <Route path="reports/blocked-users" element={wrap(<BlockedUsersReportPage />)} />
        <Route path="reports/ledger" element={wrap(<LedgerReportPage />)} />
        <Route path="reports/bonus" element={wrap(<BonusReportPage />)} />
        <Route path="settings" element={wrap(<TenantSettingsPage />)} />
        <Route path="settings/payment-gateway" element={wrap(<PaymentGatewayPage />)} />
        <Route path="settings/security" element={wrap(<SecuritySettingsPage />)} />
        <Route path="settings/api" element={wrap(<ApiConfigPage />)} />
        <Route path="settings/finance" element={wrap(<FinanceSetupPage />)} />
        <Route path="settings/platforms" element={wrap(<PlatformsSetupPage />)} />
        <Route path="settings/banner" element={wrap(<BannerSetupPage />)} />
        <Route path="settings/bank" element={wrap(<BankSettingPage />)} />
        <Route path="settings/currency" element={wrap(<CurrencySetupPage />)} />
        <Route path="settings/currency-pair" element={wrap(<CurrencyPairPage />)} />
        <Route path="settings/otp" element={wrap(<OtpSetupPage />)} />
        <Route path="settings/chatbot" element={wrap(<ChatbotPage />)} />
        <Route path="settings/mobile" element={wrap(<MobileAppPage />)} />
        <Route path="settings/software" element={wrap(<SoftwarePage />)} />
        <Route path="settings/trader" element={wrap(<TraderConfigPage />)} />
        <Route path="settings/trader-sidebar" element={wrap(<TraderSidebarSettingsPage />)} />
        <Route path="settings/notif-templates" element={wrap(<NotifTemplatesPage />)} />
        <Route path="settings/notif-user" element={wrap(<NotifUserPage />)} />
        <Route path="settings/notif-admin" element={wrap(<NotifAdminPage />)} />
        <Route path="risk/dashboard" element={wrap(<RiskDashboardPage />)} />
        <Route path="risk/exposure" element={wrap(<RiskExposurePage />)} />
        <Route path="risk/margin" element={wrap(<RiskMarginPage />)} />
        <Route path="risk/anti-hedge" element={wrap(<RiskAntiHedgePage />)} />
        <Route path="risk/suspicious" element={wrap(<RiskSuspiciousPage />)} />
        <Route path="risk/reports" element={wrap(<RiskReportsPage />)} />
        <Route path="compliance/fatca" element={wrap(<CompFatcaPage />)} />
        <Route path="compliance/crs" element={wrap(<CompCrsPage />)} />
        <Route path="compliance/mifid" element={wrap(<CompMifidPage />)} />
        <Route path="compliance/aml" element={wrap(<CompAmlPage />)} />
        <Route path="compliance/sar" element={wrap(<CompSarPage />)} />
        <Route path="compliance/sanctions" element={wrap(<CompSanctionsPage />)} />
        <Route path="compliance/calendar" element={wrap(<CompCalendarPage />)} />

        <Route path="reports/external-fund-transfer" element={wrap(<RepExternalFundTransfer />)} />
        <Route path="reports/individual-ledger" element={wrap(<RepIndividualLedger />)} />
        <Route path="reports/call-logs" element={wrap(<RepCallLogs />)} />
        <Route path="reports/credit" element={wrap(<CreditReportPage />)} />

        <Route path="finance/balance" element={wrap(<FinBalance />)} />
        <Route path="finance/credit" element={wrap(<FinCredit />)} />
        <Route path="finance/fund" element={wrap(<FinFund />)} />
        <Route path="finance/admin-deposit" element={wrap(<FinAdminDeposit />)} />
        <Route path="finance/admin-withdraw" element={wrap(<FinAdminWithdraw />)} />

        <Route path="fund-transfer/internal" element={wrap(<FtInternal />)} />
        <Route path="fund-transfer/external" element={wrap(<FtExternal />)} />

        <Route path="requests/upi" element={wrap(<ReqUpi />)} />

        <Route path="offers/loyalty" element={wrap(<OffLoyalty />)} />
        <Route path="offers/loyalty-ledger" element={wrap(<OffLoyaltyLedger />)} />

        <Route path="categories" element={wrap(<CategoriesPage />)} />

        <Route path="kyc/settings" element={wrap(<KycSettingsPage />)} />
        <Route path="kyc/upload" element={wrap(<KycUploadPage />)} />
        <Route path="kyc/reports" element={wrap(<KycReportsPage />)} />


        <Route path="manage-client/trader-analysis" element={wrap(<ManageClientHub />)} />
        <Route path="manage-client/trader-status" element={wrap(<ManageClientHub />)} />
        <Route path="managers/analysis" element={wrap(<ManageClientHub />)} />

        <Route path="profile" element={wrap(<AdminProfilePage />)} />
        <Route path="notifications" element={wrap(<NotificationsPage />)} />
        <Route path="white-label" element={wrap(<AdminMiscHub />)} />

        <Route path="contest/dashboard" element={wrap(<ContestDashboardPage />)} />
        <Route path="contest/create" element={wrap(<CreateContestPage />)} />

        <Route path="groups/list" element={wrap(<GroupsHubPage />)} />
        <Route path="groups/create" element={wrap(<GroupsHubPage />)} />

        <Route path="ai-lead-scoring" element={wrap(<MlHubPage />)} />
        <Route path="ml/churn" element={wrap(<MlHubPage />)} />
        <Route path="ml/ltv" element={wrap(<MlHubPage />)} />
        <Route path="ml/deposit-likelihood" element={wrap(<MlHubPage />)} />
        <Route path="ml/cohort-funnel" element={wrap(<MlHubPage />)} />
        <Route path="ml/model-settings" element={wrap(<MlHubPage />)} />

        <Route path="whatsapp/inbox" element={wrap(<WhatsAppHubPage />)} />
        <Route path="whatsapp/conversation" element={wrap(<WhatsAppHubPage />)} />
        <Route path="whatsapp/templates" element={wrap(<WhatsAppHubPage />)} />
        <Route path="whatsapp/voice-notes" element={wrap(<WhatsAppHubPage />)} />
        <Route path="whatsapp/routing" element={wrap(<WhatsAppHubPage />)} />
        <Route path="whatsapp/sla" element={wrap(<WhatsAppHubPage />)} />

        <Route path="mt5/connection" element={wrap(<Mt5ConnectionPage />)} />
        <Route path="mt5/trade-actions" element={wrap(<Mt5TradeActionsPage />)} />
        <Route path="mt5/create-account" element={wrap(<Mt5CreateAccountPage />)} />
        <Route path="mt5/*" element={wrap(<Mt5HubPage />)} />
        <Route path="trade/report" element={wrap(<Mt5TradeReportPage />)} />
        <Route path="trade/*" element={wrap(<Mt5HubPage />)} />
        <Route path="accounts/live" element={wrap(<Mt5AccountsPage accountType="live" />)} />
        <Route path="accounts/*" element={wrap(<Mt5HubPage />)} />
        <Route path="pamm/*" element={wrap(<Mt5HubPage />)} />
        <Route path="ea/*" element={wrap(<Mt5HubPage />)} />

        <Route path="*" element={wrap(<ModuleStub />)} />
      </Route>

      {/* ─── Trader Portal ────────────────────────────────────────── */}
      <Route path="/trader" element={<TraderShell />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={wrap(<TraderDashboardPage />)} />
        <Route path="profile" element={wrap(<TraderProfilePage />)} />
        <Route path="banks" element={wrap(<TraderBanksPage />)} />
        <Route path="accounts" element={wrap(<TraderAccountsPage />)} />
        <Route path="history" element={wrap(<TraderTradeHistoryPage />)} />
        <Route path="account-settings" element={wrap(<TraderAccountSettingsPage />)} />
        <Route path="deposit" element={wrap(<TraderDepositPage />)} />
        <Route path="withdraw" element={wrap(<TraderWithdrawPage />)} />
        <Route path="fund-transfer" element={wrap(<TraderFundTransferPage />)} />
        <Route path="bonus" element={wrap(<TraderBonusPage />)} />
        <Route path="web-trader" element={wrap(<TraderWebTraderPage />)} />
        <Route path="support" element={wrap(<TraderSupportPage />)} />
        <Route path="kyc" element={wrap(<TraderKycPage />)} />
        <Route path="chatbot" element={wrap(<TraderChatbotPage />)} />
        <Route path="contest" element={wrap(<TraderComingSoonPage />)} />
        <Route path="ea" element={wrap(<TraderComingSoonPage />)} />
        <Route path="gamification" element={wrap(<TraderComingSoonPage />)} />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Route>

      {/* ─── RM Portal (Relationship Manager) ─────────────────────── */}
      <Route path="/manager" element={<ManagerShell />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={wrap(<ManagerDashboardPage />)} />
        <Route path="clients" element={wrap(<ManagerClientsPage />)} />
        <Route path="clients/:id" element={wrap(<ManagerClientDetailPage />)} />
        <Route path="team" element={wrap(<ManagerTeamPage />)} />
        <Route path="activity" element={wrap(<ManagerActivityPage />)} />
        <Route path="reports" element={wrap(<ManagerReportsPage />)} />
        <Route path="referral" element={wrap(<ManagerDashboardPage />)} />
        <Route path="profile" element={wrap(<ManagerDashboardPage />)} />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
